import type { LocationQuery, RouteLocationRaw } from 'vue-router'

import type {
  CallbackNotice,
  ShopifyCompletionCommand,
  ShopifyStoreReturn,
} from '../models/callback.types'
import type { ShopifyOnboardingDestinationResolver } from '../models/onboardingConnection.types'
import { CONNECTIONS_ROUTE } from '../routeTarget'

import type { OAuthCallbackFlow } from './OAuthCallbackFlow'
import { resultToast, type ResultToastBranch } from './resultToast'
import type { ShopifyInstallFlow } from './ShopifyInstallFlow'

export interface IntegrationQueryContext {
  path: string
  value: string
  query: LocationQuery
}

export interface IntegrationQueryOutcome {
  redirect: RouteLocationRaw
}

export interface IntegrationQueryPreserve {
  preserve: true
}

export type IntegrationQueryHandler = (
  context: IntegrationQueryContext,
) =>
  | void
  | IntegrationQueryOutcome
  | IntegrationQueryPreserve
  | Promise<void | IntegrationQueryOutcome | IntegrationQueryPreserve>

export type IntegrationQueryRegister = (param: string, handler: IntegrationQueryHandler) => void

export interface IntegrationQueryCallbackRuntime {
  shopify: ShopifyInstallFlow
  oauth: OAuthCallbackFlow
  notify: (notice: CallbackNotice) => void
  whenContextReady: () => Promise<void>
}

export interface IntegrationQueryCallbackOptions {
  resolveShopifyOnboardingDestination: ShopifyOnboardingDestinationResolver
}

function isDedicatedCallback(path: string): boolean {
  return path.startsWith('/callback/')
}

function stringQuery(query: LocationQuery, key: string): string {
  const raw = query[key]
  const value = Array.isArray(raw) ? raw[0] : raw
  return value ?? ''
}

function shopifyCommand(query: LocationQuery): ShopifyCompletionCommand | null {
  const shopifyContext = stringQuery(query, 'shopify_context')
  const workspaceId = stringQuery(query, 'workspace_id')
  const idempotencyKey = stringQuery(query, 'idempotency_key')
  if (!shopifyContext || !workspaceId || !idempotencyKey) return null
  return { shopifyContext, workspaceId, idempotencyKey }
}

/**
 * The shape the backend's OAuth return ACTUALLY carries (ORP-20383) — `shopify_success=1` plus the
 * store it just bound. `shopifyCommand` above describes the `/integrations/shopify/complete/`
 * contract, which no live redirect can satisfy: nothing emits `idempotency_key`, and
 * `shopify_context` is only ever minted on the `/shopify-register` branch.
 */
function shopifyStoreReturn(query: LocationQuery): ShopifyStoreReturn | null {
  const storeId = stringQuery(query, 'shop_id')
  const workspaceId = stringQuery(query, 'workspace_id')
  if (!storeId || !workspaceId) return null
  return { storeId, workspaceId }
}

export function registerIntegrationQueryCallbacks(
  register: IntegrationQueryRegister,
  runtime: IntegrationQueryCallbackRuntime,
  options: IntegrationQueryCallbackOptions,
): void {
  register('shop', async ({ path, query }) => {
    if (isDedicatedCallback(path)) return { preserve: true }
    await runtime.whenContextReady()
    if (stringQuery(query, 'page') === 'sign_up_mfa') return
    const command = shopifyCommand(query)
    if (!command || stringQuery(query, 'code') || stringQuery(query, 'token')) {
      runtime.notify({ tone: 'error', key: 'integrations.shopify.installFailed' })
      return { redirect: CONNECTIONS_ROUTE }
    }
    try {
      const outcome = await runtime.shopify.execute(command)
      return { redirect: outcome.destination }
    } catch {
      return { redirect: CONNECTIONS_ROUTE }
    }
  })

  register('success', async ({ path, value, query }) => {
    if (isDedicatedCallback(path)) return { preserve: true }
    await runtime.whenContextReady()
    const outcome = await runtime.oauth.execute({
      success: value === '1' ? '1' : '0',
      ...(stringQuery(query, 'code') ? { code: stringQuery(query, 'code') } : {}),
      ...(stringQuery(query, 'message') ? { message: stringQuery(query, 'message') } : {}),
    })
    return { redirect: outcome.destination }
  })

  register('shopify_success', async ({ path, query }) => {
    if (isDedicatedCallback(path)) return { preserve: true }
    await runtime.whenContextReady()
    const command = shopifyCommand(query)
    if (command) {
      try {
        const outcome = await runtime.shopify.execute(command)
        return { redirect: outcome.destination }
      } catch {
        return { redirect: CONNECTIONS_ROUTE }
      }
    }
    // Ordering is load-bearing. `oauth.execute` captures "did this workspace already have Shopify"
    // from local state BEFORE reloading, which is what separates a first connection from a
    // re-connect; registering the store first would make every return look pre-existing and skip
    // onboarding. The registration is a side effect, not a destination — its failure is already
    // surfaced by the flow, and the return must still land somewhere either way.
    const connection = await runtime.oauth.execute({ shopifySuccess: true })
    const storeReturn = shopifyStoreReturn(query)
    if (storeReturn) {
      try {
        await runtime.shopify.installStore(storeReturn)
      } catch {
        // Reported by ShopifyInstallFlow; swallowed only so the landing below still happens.
      }
    }
    const destination = await options.resolveShopifyOnboardingDestination(connection)
    return destination ? { redirect: destination } : undefined
  })

  registerResult(register, runtime, 'error_occured')
  registerResult(register, runtime, 'shopify-success')
  registerResult(register, runtime, 'shopify-error')
  registerResult(register, runtime, 'workspace_shopify_error')
  registerResult(register, runtime, 'connect')
}

function registerResult(
  register: IntegrationQueryRegister,
  runtime: IntegrationQueryCallbackRuntime,
  branch: ResultToastBranch,
): void {
  register(branch, async ({ path, value, query }) => {
    if (isDedicatedCallback(path)) return { preserve: true }
    if (branch === 'connect' && value !== 'shopify') return
    await runtime.whenContextReady()
    const mapped = resultToast(
      branch,
      stringQuery(query, 'message'),
      branch === 'error_occured' && value === 'true',
    )
    runtime.notify(mapped.notice)
    return mapped.redirect ? { redirect: mapped.redirect.destination } : undefined
  })
}
